import type { UploadFile } from 'antd/lib/upload/interface';
import type { SetState } from 'zustand';
import type {
  SerializedMeterModel,
  SerializedManufacturer,
  InventoryMeter,
  SerializedInventoryMeterApiKey,
  SerializedInventoryLocationBuilding,
  SerializedThirdParty,
} from '@enmon/commons/lib/tools';
import { Moment } from 'moment';
import { MeterOwnerType, SerializedMeter } from '@enmon/commons/lib/enmon';
import { Commodity } from '@enmon/commons/lib/shared';
import type { InventorizeMeterStore } from '../../useStore';
import type { MeterModelSuggestions } from '../../../../hooks/meter_models/useMeterModelDetails';

const INITIAL_STATE: LocationStepForm = {
  buildingFloor: 0,
  calibrated: undefined,
  apiKey: undefined,
  internalSupplyPointCode: undefined,
  locationBuilding: undefined,
  manufactured: undefined,
  manufacturer: undefined,
  manufacturers: [],
  meterModel: undefined,
  meterModelSuggestions: undefined,
  meterOwner: undefined,
  meterOwnerType: undefined,
  meterTenant: undefined,
  commodity: undefined,
  monitoredEntity: undefined,
  newMeter: undefined,
  photoContextList: undefined,
  submeter: undefined,
  thirdParty: undefined,
};

export interface LocationStepForm {
  buildingFloor?: SerializedMeter['buildingFloor'];
  calibrated?: Moment;
  apiKey?: SerializedInventoryMeterApiKey['api_key'];
  locationBuilding?: SerializedInventoryLocationBuilding['id'] | 'empty';
  manufactured?: Moment;
  manufacturer?: SerializedManufacturer['id'];
  manufacturers?: SerializedManufacturer[];
  meterModel?: SerializedMeterModel['id'];
  meterModelSuggestions?: MeterModelSuggestions;
  meterOwner?: SerializedMeter['owner'];
  meterOwnerType?: MeterOwnerType;
  meterTenant?: InventoryMeter['tenant'];
  commodity?: Commodity;
  monitoredEntity?: string;
  newMeter?: number | null;
  photoContextList?: UploadFile[];
  submeter?: number | null;
  internalNote?: string;
  internalSupplyPointCode?: string;
  thirdParty?: SerializedThirdParty['name'];
}

export type LocationStepSlice = LocationStepForm & {
  resetLocationStepSlice: () => void;
  resetSubmeter: () => void;
  setLocationStepValues: (values: LocationStepForm) => void;
  setManufacturer: (payload?: SerializedManufacturer['id']) => void;
  setMeterModel: (payload?: SerializedMeterModel['id']) => void;
  setMeterModelSuggestions: (payload?: MeterModelSuggestions) => void;
  setPhotoContextList: (payload: UploadFile[]) => void;
};

export const createLocationStepSlice = (set: SetState<InventorizeMeterStore>): LocationStepSlice => ({
  ...INITIAL_STATE,

  setManufacturer: (payload?: SerializedManufacturer['id']) =>
    set(() => ({
      manufacturer: payload,
    })),
  setMeterModel: (payload?: SerializedMeterModel['id']) =>
    set(() => ({
      meterModel: payload,
    })),
  setMeterModelSuggestions: (payload?: MeterModelSuggestions) =>
    set(() => ({
      meterModelSuggestions: payload,
    })),
  setPhotoContextList: (payload: UploadFile[]) =>
    set(() => ({
      photoContextList: payload,
    })),
  setLocationStepValues: (values: LocationStepForm) => set(values),
  resetLocationStepSlice: () => set(INITIAL_STATE),
  resetSubmeter: () => set({ submeter: undefined }),
});
